import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ArtistUnuseTickets,
  ArtistUnuseTicketsMutation,
  ArtistUnuseTicketsMutationVariables,
  ArtistUseTickets,
  ArtistUseTicketsMutation,
  ArtistUseTicketsMutationVariables,
  ConsoleUserOwnedItemDetailQuery,
  ConsoleUserOwnedItemDetailQueryVariables,
  UserItemDetailBoxFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { useCurrentUser } from '@/core/auth'
import { convertPortfolioItemToSku } from '@/core/sku'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']

export const useViewModel = () => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{
    userId: string
    itemId: string
    skuId?: string
    throughTicketIndex?: string
  }>()
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleUserOwnedItemDetail')
  const { data, loading } = useQuery<
    ConsoleUserOwnedItemDetailQuery,
    ConsoleUserOwnedItemDetailQueryVariables
  >(QUERY, {
    variables: {
      itemId: router.query.itemId,
      userId: router.query.userId,
    },
  })

  const [artistUseTicket, { loading: using }] = useMutation<
    ArtistUseTicketsMutation,
    ArtistUseTicketsMutationVariables
  >(ArtistUseTickets)

  const [artistUnuseTicket, { loading: unusing }] = useMutation<
    ArtistUnuseTicketsMutation,
    ArtistUnuseTicketsMutationVariables
  >(ArtistUnuseTickets)

  const portfolio = useMemo(() => {
    return convertPortfolioItemToSku(
      data?.artistPortfolio ?? null,
      router.query.skuId
    )
  }, [data?.artistPortfolio, router.query.skuId])

  // 最初に表示する使用済みチケットシリアルのID配列
  const initialSelectedSerialIds = useMemo(() => {
    if (router.query.throughTicketIndex) {
      return (
        portfolio?.serials
          ?.filter(
            ({ serial }) =>
              !!router.query.throughTicketIndex &&
              serial?.ticketStatus?.throughTicket?.[
                +router.query.throughTicketIndex
              ]?.isUsed
          )
          .map(({ id }) => id) ?? []
      )
    }
    return (
      portfolio?.serials
        ?.filter(({ serial }) => serial?.ticketStatus?.isUsed)
        .map(({ id }) => id) ?? []
    )
  }, [portfolio?.serials, router.query.throughTicketIndex])

  const thoughTicket = useMemo(() => {
    if (router.query.throughTicketIndex === undefined) {
      return undefined
    }
    return data?.artistPortfolio?.item?.metadata?.ticket?.throughTicket?.[
      +router.query.throughTicketIndex
    ]
  }, [
    data?.artistPortfolio?.item?.metadata?.ticket?.throughTicket,
    router.query.throughTicketIndex,
  ])

  return {
    state: {
      loading,
      artistId: user?.uid ?? '',
      submitting: using || unusing,
      portfolio,
      thoughTicket,
      throughTicketIndex: router.query.throughTicketIndex,
      initialSelectedSerialIds,
    },
    actions: {
      async onUseTicketsBluk(useSerialIds: string[], unuseSerialIds: string[]) {
        // 入力値のうち、初期とデータが変わらないものを除外
        const useInputIds = useSerialIds.filter(
          id => !initialSelectedSerialIds.includes(id)
        )
        const unuseInputIds = unuseSerialIds.filter(id =>
          initialSelectedSerialIds.includes(id)
        )

        if (!useInputIds.length && !unuseInputIds.length) {
          return
        }

        return Promise.all([
          useInputIds.length
            ? artistUseTicket({
                variables: {
                  inputs: {
                    items: [
                      {
                        itemId: router.query.itemId,
                        serialIds: useInputIds,
                        throughTicketName: thoughTicket?.name,
                      },
                    ],
                  },
                },
              })
            : null,
          unuseInputIds.length
            ? artistUnuseTicket({
                variables: {
                  inputs: {
                    items: [
                      {
                        itemId: router.query.itemId,
                        serialIds: unuseInputIds,
                        throughTicketName: thoughTicket?.name,
                      },
                    ],
                  },
                },
              })
            : null,
        ])
          .then(() =>
            showMessage(t('success', 'チケット使用状況を変更しました'))
          )
          .catch(showError)
      },
    },
  }
}

const QUERY = gql`
  query consoleUserOwnedItemDetail(
    $itemId: ID!
    $userId: String!
    $skuId: String
  ) {
    artistPortfolio(id: $itemId, userId: $userId) {
      ...UserItemDetailBox
    }
  }
  ${UserItemDetailBoxFragmentDoc}
`
